import { Layout, CheckSquare, FileText, Users, Settings, Moon, DoorOpen, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { ViewType } from "@/pages/App";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useUser } from "@/hooks/useUser";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useTheme } from "next-themes";
import { useNavigate } from "react-router-dom";
import { RoomParticipants } from "./RoomParticipants";
import { Button } from "./ui/button";
import { useToast } from "./ui/use-toast";
import { supabase } from "@/lib/supabase";
import { useEffect, useState } from "react";

interface SidebarProps {
  activeView: ViewType;
  onViewChange: (view: ViewType) => void;
  onParticipantsChange?: (count: number) => void;
}

export const Sidebar = ({ activeView, onViewChange, onParticipantsChange }: SidebarProps) => {
  const { user } = useUser();
  const [isMembersOpen, setIsMembersOpen] = useState(false);
  const { toast } = useToast();
  const [isCreator, setIsCreator] = useState(false);
  const navItems = [
    {
      id: 'board' as ViewType,
      label: 'Board',
      icon: Layout,
      description: 'Visual planning canvas',
      longDescription: 'Create and organize your ideas with our visual canvas. Draw shapes, add text, and collaborate in real-time.'
    },
    {
      id: 'todo' as ViewType,
      label: 'Todo',
      icon: CheckSquare,
      description: 'Task management',
      longDescription: 'Manage your tasks and track progress. Create, organize, and prioritize your to-do items.'
    },
    {
      id: 'notes' as ViewType,
      label: 'Notes',
      icon: FileText,
      description: 'Quick notes & ideas',
      longDescription: 'Jot down ideas, meeting notes, or anything else that comes to mind. Your notes are automatically saved.'
    }
  ];
  // No navigation or URL change for Access Control
  
  // Get room ID and username from URL
  const searchParams = new URLSearchParams(window.location.search);
  const roomId = searchParams.get('room') || '';
  const urlUser = searchParams.get('user') || '';
  const [roomName, setRoomName] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  
  // Fetch room data to check if current user is the creator and get room name
  useEffect(() => {
    if (!roomId || !urlUser) return;
    
    const fetchRoomData = async () => {
      try {
        const { data: room, error } = await supabase
          .from('rooms')
          .select('creator_name, name')
          .eq('id', roomId)
          .single();
          
        if (error) throw error;
        
        // setRoomName(room?.name || '');
        
        // Check if current user is the creator of the room
        setIsCreator(room?.creator_name === urlUser);
      } catch (error) {
        console.error('Error checking room creator:', error);
        setIsCreator(false);
      }
    };
    
    fetchRoomData();
  }, [roomId, urlUser]);

  return (
    <div className="w-72 h-screen bg-white/80 backdrop-blur-sm border-r border-gray-100 flex flex-col overflow-hidden relative z-0">
      {/* Hand-drawn border effect */}
      <div className="absolute inset-0 border-r-2 border-gray-100 pointer-events-none" style={{
        borderImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' viewBox=\'0 0 100 100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0,0 L100,0 L100,100 L0,100 Z\' fill=\'none\' stroke=\'%23e5e7eb\' stroke-width=\'2\' stroke-dasharray=\'6,4\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E") 2',
        borderImageWidth: '0 2px 0 0',
        borderImageOutset: '0 1 0 0',
        borderImageRepeat: 'stretch',
      }} />
      
      <div className="p-4 border-b border-gray-100 relative z-10 bg-white/80 backdrop-blur-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <img 
                src="/sticky-note.svg" 
                alt="Made for teams Logo" 
                className="h-8 w-8" 
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTAgMTBIMzBWMTBDMzAgMTAgMjggMTIgMjUgMTJDMjIgMTIgMjAgMTAgMjAgMTBDMjAgMTAgMTggMTIgMTUgMTJDMTIgMTIgMTAgMTAgMTAgMTBaIiBmaWxsPSIjRkZFQjNCIi8+PHBhdGggZD0iTTMwIDI1QzMwIDI1IDI4IDI3IDI1IDI3QzIyIDI3IDIwIDI1IDIwIDI1QzIwIDI1IDE4IDI3IDE1IDI3QzEyIDI3IDEwIDI1IDEwIDI1VjEwQzEwIDEwIDEyIDEyIDE1IDEyQzE4IDEyIDIwIDEwIDIwIDEwQzIwIDEwIDIyIDEyIDI1IDEyQzI4IDEyIDMwIDEwIDMwIDEwVjI1WiIgZmlsbD0iI0ZGRUIzQiIvPjxwYXRoIGQ9Ik0zMCAyNUwyMi41IDI1TDIyLjUgMzIuNUMzMi41IDI1IDMwIDI1IDMwIDI1WiIgZmlsbD0iI0ZGRjBBMCIvPjxwYXRoIGQ9Ik0xNSAxNUgyNSIgc3Ryb2tlPSJibGFjayIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLW9wYWNpdHk9IjAuMiIvPjxwYXRoIGQ9Ik0xNSAyMEgyNSIgc3Ryb2tlPSJibGFjayIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLW9wYWNpdHk9IjAuMiIvPjxwYXRoIGQ9Ik0xNSAyNUgyMCIgc3Ryb2tlPSJibGFjayIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLW9wYWNpdHk9IjAuMiIvPjxwYXRoIGQ9Ik0xMCAxMEgzMFYyNUMzMCAyNSAyOCAyNyAyNSAyN0MyMiAyNyAyMCAyNSAyMCAyNUMyMCAyNSAxOCAyNyAxNSAyN0MxMiAyNyAxMCAyNSAxMCAyNVYxMFoiIHN0cm9rZT0iYmxhY2siIHN0cm9rZS13aWR0aD0iMC41IiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIHN0cm9rZS1kYXNoYXJyYXk9IjIgMSIvPjwvc3ZnPg==';
                }}
              />
              <div className="absolute -inset-1.5 bg-gray-100/50 rounded-full -z-10"></div>
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Made For Teams</h2>
          </div>
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <button className="text-gray-400 hover:text-gray-600 transition-colors">
                    <Settings className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Settings</p>
              </TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem 
                onClick={() => {
                  toast({
                    title: 'Coming soon',
                    description: 'Theme settings will be available in a future update',
                  });
                }}
              >
                <Moon className="mr-2 h-4 w-4" />
                <span>Theme</span>
              </DropdownMenuItem>



              <DropdownMenuItem 
                onClick={async () => {
                  if (window.confirm('Are you sure you want to leave this room? This will remove you from the participants list.')) {
                    try {
                      // Get the current session token
                      const sessionToken = localStorage.getItem(`session_token_${roomId}`);
                      
                      if (!sessionToken) {
                        throw new Error('Session not found');
                      }
                      
                      // Remove the user from the room_participants table using session token
                      const { error } = await supabase
                        .from('room_participants')
                        .delete()
                        .eq('room_id', roomId)
                        .eq('session_token', sessionToken);

                      if (error) throw error;
                      
                      // Clear the session token from local storage
                      localStorage.removeItem(`session_token_${roomId}`);

                      // Show success message
                      toast({
                        title: 'Left room',
                        description: 'You have successfully left the room.',
                      });
                      
                      // Redirect to home page after a short delay
                      setTimeout(() => {
                        window.location.href = '/';
                      }, 1000);
                      
                    } catch (error) {
                      console.error('Error leaving room:', error);
                      toast({
                        title: 'Error',
                        description: 'Failed to leave the room. Please try again.',
                        variant: 'destructive',
                      });
                    }
                  }
                }}
                className="text-red-600 focus:bg-red-50 focus:text-red-700"
              >
                <DoorOpen className="mr-2 h-4 w-4" />
                <span>Leave room</span>
              </DropdownMenuItem>
              
              {/* Delete Room Button - Only visible to room creator */}
              {isCreator && (
                <DropdownMenuItem 
                  onClick={async () => {
                    if (window.confirm('⚠️ WARNING: This will permanently delete this room and all its data. All users will be disconnected. This action cannot be undone.\n\nAre you sure you want to delete this room?')) {
                      try {
                        // Get the room data to verify ownership by username
                        const { data: roomData, error: roomError } = await supabase
                          .from('rooms')
                          .select('creator_name')
                          .eq('id', roomId)
                          .single();
                        
                        if (roomError) throw roomError;
                        
                        // Verify the current user is the room creator by username
                        if (roomData.creator_name !== urlUser) {
                          throw new Error('Only the room creator can delete the room');
                        }
                        
                        // Delete all related data in the correct order to respect foreign key constraints
                        // 1. Delete participants
                        await supabase
                          .from('room_participants')
                          .delete()
                          .eq('room_id', roomId);
                        
                        // 2. Delete kicked users
                        await supabase
                          .from('room_kicked')
                          .delete()
                          .eq('room_id', roomId);
                        
                        // 3. Delete the room itself
                        const { error: deleteError } = await supabase
                          .from('rooms')
                          .delete()
                          .eq('id', roomId);
                        
                        if (deleteError) throw deleteError;
                        
                        // Show success message
                        toast({
                          title: 'Room deleted',
                          description: 'The room has been successfully deleted.',
                        });
                        
                        // Clear the session token from local storage
                        localStorage.removeItem(`session_token_${roomId}`);
                        
                        // Redirect to home page after a short delay
                        setTimeout(() => {
                          window.location.href = '/';
                        }, 1000);
                        
                      } catch (error) {
                        console.error('Error deleting room:', error);
                        toast({
                          title: 'Error',
                          description: error instanceof Error ? error.message : 'Failed to delete the room. Please try again.',
                          variant: 'destructive',
                        });
                      }
                    }
                  }}
                  className="text-red-600 focus:bg-red-50 focus:text-red-700"
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  <span>Delete Room</span>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Navigation */}
      <TooltipProvider delayDuration={100}>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.id;
            
            return (
              <Tooltip key={item.id}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => onViewChange(item.id)} // Only in-app view change
                    className={`group w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all ${
                      isActive
                        ? 'bg-gray-50 text-gray-900 font-medium shadow-sm'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 hover:shadow-sm'
                    } relative overflow-hidden`}
                  >
                    <span className={`absolute left-0 top-0 h-full w-1 ${
                      isActive ? 'bg-gray-900' : 'bg-transparent group-hover:bg-gray-200'
                    } transition-colors`} />
                    <Icon className={`h-4.5 w-4.5 mr-3 flex-shrink-0 ${
                      isActive ? 'text-gray-900' : 'text-gray-500 group-hover:text-gray-700'
                    }`} />
                    <div className="flex-1 text-left">
                      <div className="font-medium">{item.label}</div>
                      <div className="text-xs text-gray-500 truncate">{item.description}</div>
                    </div>
                    {isActive && (
                      <div className="w-1.5 h-1.5 bg-gray-900 rounded-full" />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <h4 className="font-medium mb-1">{item.label}</h4>
                  <p className="text-sm text-gray-600">{item.longDescription}</p>
                </TooltipContent>
              </Tooltip>
            );
          })}
          
          {/* Members Section */}
          <div className="mt-6 border-t border-gray-100 pt-4">
            {roomId && urlUser && (
              <RoomParticipants 
                roomId={roomId}
                currentUser={urlUser}
                showHeader={false}
                isCreator={isCreator}
                onParticipantsChange={onParticipantsChange}
              />
            )}
          </div>
        </nav>
      </TooltipProvider>

      {/* Room ID and Share Button */}
      <div className="mt-auto border-t border-gray-100 pt-3 px-3 pb-4">
        <div className="flex flex-col space-y-2">
          <div className="flex items-center space-x-1">
            <p className="text-xs text-gray-500">Room Code</p>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="text-gray-400 hover:text-gray-600 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="w-64">
                <p className="text-sm whitespace-normal">This room will be automatically deleted after 24 hours of inactivity.</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex items-center justify-between bg-gray-50 rounded-md p-2">
            <div className="flex-1 min-w-0">
              <p className="text-lg font-bold font-mono text-gray-800 tracking-wider">
                {roomId}
              </p>
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(roomId).then(() => {
                  toast({
                    title: 'Copied!',
                    description: 'Room ID copied to clipboard',
                  });
                });
              }}
              className="ml-2 p-1.5 rounded-md hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors"
              title="Copy room ID"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
